import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewContainerRef
} from '@angular/core'
import {AddToCartComponent} from '@spartacus/cart/base/components/add-to-cart'
import {ActiveCartFacade, CartUiEventAddToCart} from '@spartacus/cart/base/root'
import {CmsAddToCartComponent, EventService} from '@spartacus/core'
import {CmsComponentData, CurrentProductService, LAUNCH_CALLER, LaunchDialogService} from '@spartacus/storefront'
import {combineLatest, Observable, Subject, Subscription} from 'rxjs'
import {filter, take} from 'rxjs/operators'
import {EbAnalyticsService} from 'src/app/features/analytics/services/facades/eb-analytics.service'
import {EbAuthService} from '../../../auth/services/eb-auth.service'
import {EbProduct} from '../../../product/interfaces/eb-product.interface'
import {EbOrderEntry} from '../../interface/eb-order-entry.interface'

@Component({
    selector: 'eb-add-to-cart',
    templateUrl: './eb-add-to-cart.component.html',
    styleUrls: ['./eb-add-to-cart.component.scss'],
    standalone: false
})
export class EbAddToCartComponent extends AddToCartComponent implements OnInit, OnDestroy {
  @Input() showQuantity = true
  @Input() product: EbProduct = {}
  @Input() isAvailable: boolean
  @Input() hasSuccessor: boolean
  @Input() iconOnly = false
  @Input() hasPrice = false
  @Input() showCountOfProductInCart = false
  @Input() favoritesView = false
  @Input() purchaseProduct = false
  @Input() productPrice: number
  @Output() newQuantity: EventEmitter<number> = new EventEmitter()
  ebSubscription = new Subscription()
  maxQuantity = 1000
  count = 0
  minOrderQuantity: number
  quantityKey = 'quantity'
  cartEntries$: Observable<EbOrderEntry[]>;
  notifier$ = new Subject<void>();

  get addToCartTooltipI18NKey() {
    if (this.hasSuccessor && !this.product.orderable) {
      return 'addToCart.addSuccessorToCart'
    } else {
      if (this.showCountOfProductInCart && this.count > 0) {
        return 'addToCart.increaseInCart'
      } else {
        return 'addToCart.addToCart'
      }
    }
  }

  ngOnInit() {
    super.ngOnInit()
    this.setMinOrderQuantity()
    this.cartEntries$ = this.activeCartService.getEntries()
    this.ebSubscription.add(
      this.addToCartForm.valueChanges.subscribe(change => {
        if (change.quantity) {
          this.updatePrice(change.quantity)
        }
      })
    )

    this.ebSubscription.add(
      this.activeCartService.getEntries().subscribe(entries => {
        entries
          .filter(entry => entry.product.code === this.productCode)
          .forEach((entry: EbOrderEntry) => {
            if (this.purchaseProduct && !this.favoritesView) {
              this.count = 1
            } else {
              this.count = Math.ceil(entry.quantity / this.minOrderQuantity)
              this.addToCartForm.controls[this.quantityKey].setValue(
                Math.ceil(this.count / this.minOrderQuantity)
              )
            }
          })
        this.cd.detectChanges()
      })
    )
  }

  constructor(
    currentProductService: CurrentProductService,
    cd: ChangeDetectorRef,
    activeCartService: ActiveCartFacade,
    eventService: EventService,
    component: CmsComponentData<CmsAddToCartComponent>,
    public modalService: LaunchDialogService,
    public vcr: ViewContainerRef,
    public ebElement: ElementRef,
    public authService: EbAuthService,
    public ebAnalyticsService: EbAnalyticsService
  ) {
    super(currentProductService, cd, activeCartService, component, eventService)
  }

  updatePrice(newQuantity: number) {
    this.newQuantity.emit(newQuantity)
  }

  addProductToCart(evt) {
    let quantity =
      evt.currentTarget?.[1]?.valueAsNumber || this.addToCartForm.get(this.quantityKey).value
    if (this.iconOnly || this.favoritesView) {
      quantity = 1
    }

    quantity = quantity * this.minOrderQuantity
    if (!this.productCode || quantity <= 0) {
      return
    }
    combineLatest([this.activeCartService.getEntries(), this.authService.isUserLoggedIn()])
      .pipe(
        take(1),
        filter(([, isUserLoggedIn]) => isUserLoggedIn)
      )
      .subscribe(([entries]) => {
        this.ebOpenModal()
        this.activeCartService.addEntry(this.productCode, quantity)
        this.handleAddToCartEvent(this.productPrice.toString())
      })
  }

  protected ebOpenModal() {
    this.activeCartService
      .getEntries()
      .pipe(take(1))
      .subscribe(cartEntries => {
        const data: CartUiEventAddToCart = new CartUiEventAddToCart()
        data.productCode = this.productCode
        data.numberOfEntriesBeforeAdd = cartEntries.length
        data.quantity = this.quantity
        const dialog = this.modalService.openDialog(
          LAUNCH_CALLER.ADDED_TO_CART,
          this.ebElement,
          this.vcr,
          data
        )
        dialog?.pipe(take(1)).subscribe()
      })
  }

  setMinOrderQuantity() {
    this.minOrderQuantity = this.product?.productSalesView?.minOrderQuantity
      ? this.product?.productSalesView?.minOrderQuantity
      : 1
  }

  handleAddToCartEvent(productPrice:string) {
    this.ebSubscription.add(
      combineLatest([this.activeCartService.isStable(),this.activeCartService.getEntries()])
        .pipe(filter(([isStable]) => isStable), take(1))
        .subscribe(([,entries]) => {
          if(this.iconOnly){
            this.ebAnalyticsService.addEcommerceProductToCart(entries, productPrice, "Ecommerce", "Add-to-cart", "Parent Page")
          } else if(this.favoritesView){
            this.ebAnalyticsService.addEcommerceProductToCart(entries, productPrice, "Ecommerce", "Add-to-cart", "Favorites")
          } else {
            this.ebAnalyticsService.addEcommerceProductToCart(entries, productPrice, "Ecommerce", "Add-to-cart", "Variant Page")
          }
        })
    )
  }

  ngOnDestroy() {
    super.ngOnDestroy()
    if (this.ebSubscription) {
      this.ebSubscription.unsubscribe()
    }
  }
}
